/**
 * Auto-Repair Section Component
 * 
 * Displays the self-healing system status, statistics, and
 * recent auto-repair attempts log.
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  HeartPulse, RefreshCcw, BarChart3, Clock, Sparkles, 
  CheckCircle2, AlertTriangle, RotateCcw 
} from "lucide-react";
import { RepairResultColors, formatTime, getAgentName, getAgentAvatar } from "./constants";

export function AutoRepairSection({ stats, attempts, onRefresh }) {
  return (
    <div className="space-y-6" data-testid="auto-repair-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/15 rounded-lg">
            <HeartPulse className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Sistema de Auto-Repair</h2>
            <p className="text-sm text-muted-foreground">
              Self-healing automatico - problemas corrigidos sem intervenção manual
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-auto-repair">
          <RefreshCcw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      {stats ? (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card className="bg-card border-border/50">
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-blue-400" data-testid="stat-detected">
                {stats.total_attempts || 0}
              </p>
              <p className="text-xs text-muted-foreground">Problemas Detectados</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-green-400" data-testid="stat-auto-fixed">
                {stats.successes || 0}
              </p>
              <p className="text-xs text-muted-foreground">Corrigidos Auto</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-red-400" data-testid="stat-failed">
                {stats.failures || 0}
              </p>
              <p className="text-xs text-muted-foreground">Falhas</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-amber-400" data-testid="stat-escalated">
                {stats.escalated || 0}
              </p>
              <p className="text-xs text-muted-foreground">Escalados</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-emerald-400" data-testid="stat-success-rate">
                {stats.success_rate || 0}%
              </p>
              <p className="text-xs text-muted-foreground">Taxa de Sucesso</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-purple-400" data-testid="stat-period">
                {stats.period_hours || 24}h
              </p>
              <p className="text-xs text-muted-foreground">Periodo</p>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <RotateCcw className="w-8 h-8 mx-auto animate-spin mb-2" />
          Carregando estatisticas...
        </div>
      )}

      {/* Stats by Agent */}
      {stats?.by_rule && stats.by_rule.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Execucoes por Regra
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.by_rule.map((item, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-2 rounded-lg bg-muted/30"
                >
                  <span className="text-sm font-medium">{item._id}</span>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-green-400">{item.successes} ok</span>
                    <span className="text-red-400">{item.failures} falhas</span>
                    <span className="text-muted-foreground">Total: {item.total}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Attempts Log */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Historico de Acoes (Ultimas 24h)
          </CardTitle>
          <CardDescription>
            Log das tentativas de auto-reparo executadas pelos agentes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {attempts && attempts.length > 0 ? (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {attempts.map((attempt, idx) => {
                const resultColor = RepairResultColors[attempt.result] || RepairResultColors.failed;
                const agentInfo = attempt.rule_id?.split("_")[0] || "tech";
                
                return (
                  <div 
                    key={attempt.attempt_id || idx}
                    className="flex items-center gap-4 p-3 rounded-lg bg-muted/20 border border-border/30 hover:bg-muted/30 transition-colors"
                    data-testid={`attempt-${idx}`}
                  >
                    {/* Timestamp */}
                    <div className="w-28 flex-shrink-0">
                      <span className="text-xs text-muted-foreground">
                        {formatTime(attempt.started_at)}
                      </span>
                    </div>
                    
                    {/* Agent */}
                    <div className="w-24 flex-shrink-0 flex items-center gap-2">
                      <span className="text-lg">{getAgentAvatar(agentInfo)}</span>
                      <span className="text-xs font-medium">{getAgentName(agentInfo)}</span>
                    </div>
                    
                    {/* Error Type */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{attempt.error_type}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        Regra: {attempt.rule_id}
                      </p>
                    </div>
                    
                    {/* Restaurant */}
                    {attempt.restaurant_id && (
                      <div className="w-32 flex-shrink-0">
                        <span className="text-xs text-muted-foreground truncate block">
                          {attempt.restaurant_id.slice(0, 12)}...
                        </span>
                      </div>
                    )}
                    
                    {/* Result Badge */}
                    <div className="w-24 flex-shrink-0 flex justify-end">
                      <Badge className={`${resultColor} text-xs`}>
                        {attempt.result === "success" ? "Corrigido" : 
                         attempt.result === "failed" ? "Falhou" :
                         attempt.result === "partial" ? "Parcial" : "Ignorado"}
                      </Badge>
                    </div>
                    
                    {/* Escalated indicator */}
                    {attempt.escalated && (
                      <div className="w-8 flex-shrink-0">
                        <AlertTriangle className="w-4 h-4 text-amber-400" title="Escalado para alerta" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4 opacity-50 text-green-400" />
              <p className="text-lg font-medium">Nenhuma acao de auto-repair registrada</p>
              <p className="text-sm mt-1">
                O sistema esta operando normalmente ou nenhum problema foi detectado
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* How it works */}
      <Card className="bg-gradient-to-br from-emerald-500/5 to-transparent border-emerald-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-emerald-500/15 rounded-xl">
              <Sparkles className="w-8 h-8 text-emerald-400" />
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold text-lg">Como funciona o Auto-Repair?</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>1. <strong>Deteccao:</strong> O sistema monitora eventos de erro em tempo real</p>
                <p>2. <strong>Analise:</strong> Verifica se existe uma regra de auto-reparo aplicavel</p>
                <p>3. <strong>Execucao:</strong> O agente responsavel tenta corrigir automaticamente</p>
                <p>4. <strong>Resultado:</strong> Se sucesso → log apenas | Se falha → escala para alerta</p>
              </div>
              <div className="flex gap-2 pt-2">
                <Badge variant="outline" className="text-emerald-400 border-emerald-500/30">
                  12 regras ativas
                </Badge>
                <Badge variant="outline" className="text-blue-400 border-blue-500/30">
                  3 agentes
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
